/* eslint-disable react/no-unstable-nested-components */
import classnames from 'classnames';
import { forwardRef, memo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { DataEffect, UIEffect } from '@/composers';
import FallbackError from '@/features/reload-report-tab';
import { ProgressLoading, OverlayLoading } from '@/shared/ui';
import { STRIP_MANAGEMENT_TAB_ID } from '@/entities/report';
import StripLeftColumn from './stripLeftColumn';
import WrapReport from './wrapReport';
import useStripsManagementV2 from './useStripsManagementV2';

export type StripsManagementV2Handle = {
  isChangedData: () => boolean;
  onClickSaveReport: () => Promise<void>;
  resetData: () => void;
};

export type StripsManagementV2Props = {
  activeTab?: any;
  isActive?: boolean;
  needStartedAI?: boolean;
  isNotEdit?: boolean;
  reportData?: any;
  ecgDataMap?: any;
  timezoneOffset?: any;
};

/**
 * Top-level strips management tab, hosting the strip list column beside the report body. useStripsManagementV2 resolves the tab key and active state; the progress loader stands in until AI processing starts when the tab requires it, and the content is wrapped in an error boundary with an inactive-tab overlay alongside the data and UI effect components.
 *
 * @param props - Tab props carrying the active tab id, report data, timezone offset, read-only mode and whether AI processing is required.
 * @param ref - Ref passed to the hook so the parent can drive this tab.
 * @returns The rendered strips management tab, or the progress loader while AI processing has not started.
 */
const StripsManagementV2 = forwardRef<StripsManagementV2Handle, StripsManagementV2Props>(
  (
    { activeTab, isActive, needStartedAI, isNotEdit, reportData, ecgDataMap, timezoneOffset },
    ref,
  ) => {
    const { keyRecoil, isStartedAiProcessing, isShowTabHeader, isTabActive } =
      useStripsManagementV2(
        { activeTab, isActive, needStartedAI, isNotEdit, reportData, ecgDataMap, timezoneOffset },
        ref,
      );

    if (!isStartedAiProcessing && needStartedAI) {
      return <ProgressLoading isStartedAiProcessing={isStartedAiProcessing} />;
    }

    return (
      <ErrorBoundary
        fallbackRender={({ error, resetErrorBoundary }) => (
          <FallbackError
            error={error}
            resetErrorBoundary={resetErrorBoundary}
            isShowTabHeader={isShowTabHeader}
          />
        )}
      >
        <div
          id={STRIP_MANAGEMENT_TAB_ID}
          className={classnames('report-summary-tab', isShowTabHeader ? '' : '--hide-tab')}
        >
          <OverlayLoading isVisible={!isTabActive} />
          <DataEffect tab={activeTab} keyRecoil={keyRecoil} />
          <UIEffect tab={activeTab} keyRecoil={keyRecoil} />
          <StripLeftColumn />
          <WrapReport
            timezoneOffset={timezoneOffset}
            reportData={reportData}
            isActive={isActive}
            isNotEdit={isNotEdit}
          />
        </div>
      </ErrorBoundary>
    );
  },
);

export default memo(StripsManagementV2);
